import { labOptions, Signage } from '../../Signage';
import * as Platform from '../../Platform';
import { Log } from '../../AuxillaryService/Log';
import { ReachAPI } from '../../ReachApi';
import { WidgetType } from '../types';
import { WidgetWithScheduler } from '../WidgetWithScheduler';
import { AdBundle } from './AdBundle';
import {
    AdAssetInformation,
    AdObject,
    AdWidgetConfiguration,
    AdWidgetConfigurationDetails,
    AdWidgetTimelineSlot,
    ZodAdWidgetConfigurationDetails,
} from './types';
import moment, { Moment } from 'moment';
import { FadeInCommand } from '..';
import { NamedEventCode } from '@reachmedianetwork/signage-abstractions';
import { glom } from '../../Signage/glom';
import { GetAssetInfoSuccessResponse } from '../../ReachApi/cdnTypes';

/**
 * AdWidget
 * NOTE: Regarding BrightSign:
 * - Ad widget is not currently used on BrightSign
 * - Video playback is different in BrightSign
 *
 * NOTE: Regarding videos in Chromium
 * - Loading more than two videos at any given time will cause problems
 * - Loading three videos in BrightSign causes problems
 * - This issue may be more intermittent with other players
 *
 * @todo: add functionality to have only two videos loaded at any given time
 * like AnnouncementWidget
 */
export class AdWidget extends WidgetWithScheduler {
    #adNowPlaying: boolean;
    #bundles: AdBundle[];
    #adDataChanged: boolean;
    #currentAd: AdObject | null;
    #startPlay: Moment | null;
    #stringifiedAdList: string;
    #adList: AdObject[];
    type: WidgetType;
    #schedulerActionRanOnce: boolean;
    #lastBundleIndex: number;
    #assets: Map<string, AdAssetInformation>;

    constructor(widgetConfiguration: AdWidgetConfiguration, zoneId: number) {
        super(widgetConfiguration, zoneId);

        this.type = WidgetType.AD;
        this.config = widgetConfiguration.config;

        /**
         * Number of seconds between each time the `Scheduler` runs
         */
        this.schedulerRunInterval = 60;

        /**
         * Used to track whether the schedulerAction function has ran already or not
         */
        this.#schedulerActionRanOnce = false;

        /**
         * The last bundle index used in the time-based timeline
         */
        this.#lastBundleIndex = 0;
        this.#assets = new Map();
        this.#adNowPlaying = false;
        this.#bundles = [];
        this.#adDataChanged = false;
        this.#currentAd = null;
        this.#startPlay = null;
        this.#stringifiedAdList = '';
        this.#adList = [];
    }

    protected validateConfigurationDetails(
        adWidgetConfigurationDetails: AdWidgetConfigurationDetails
    ): AdWidgetConfigurationDetails {
        return ZodAdWidgetConfigurationDetails.parse(adWidgetConfigurationDetails);
    }

    /**
     * Get assets for all ads
     */
    async getAdAssets(): Promise<void> {
        const assetPromises = this.#adList
            .filter((ad) => {
                // only assets we don't already have
                return typeof this.#assets.has(ad.asset_uuid);
            })
            .map((ad) =>
                ReachAPI.cdn
                    .getAssetInfo(ad.asset_uuid)
                    .fail(
                        (...args: [JQuery.jqXHR, string, string, string | null, Event | null]) => {
                            Log.Warning(
                                `Failed to get Ad Info for the Ads Widget:${this.id}`,
                                null,
                                'ad.js', // **See note about hardcoded source
                                ReachAPI.createPromiseFailObject.apply(this, args)
                            );
                        }
                    )
            );

        const responses: GetAssetInfoSuccessResponse[] = await Promise.all(assetPromises);

        responses.forEach((response: GetAssetInfoSuccessResponse) => {
            response.assets.forEach((asset) => {
                // Make sure the returned asset is not "undefined"
                // Undefined assets are returned when the API fails and fail block is executed
                if (typeof asset !== 'undefined') {
                    this.#assets.set(asset.uuid, asset);
                }
            });
        });
    }

    /**
     * Get ad file type
     */
    getAdFileType(ad: AdObject): string | undefined {
        const mimeType = this.#assets.get(ad.asset_uuid)?.type;
        if (typeof mimeType === 'undefined') {
            throw new Error('Could not find asset type for ad: ' + ad.asset_uuid);
        }
        const mimeTypeParts = mimeType.split('/');
        return mimeTypeParts[0] ? mimeTypeParts[0] : undefined;
    }

    /**
     * Determine asset URL based on file type
     */
    getAssetUrl(ad: AdObject): string {
        if (ad.filetype === 'video') {
            return ReachAPI.server + '/cdn/asset/' + ad.asset_uuid;
        }

        return './asset.php?id=' + ad.asset_uuid;
    }

    /**
     * Render ad element
     */
    renderAd(ad: AdObject): void {
        if (ad.filetype === 'video') {
            const videoElement: JQuery<HTMLVideoElement> = $(`
                <video
                    width="100%"
                    height="100%"
                    id="video_ad_${ad.adid}"
                    data-video-uuid="${ad.asset_uuid}"
                >
                    <source src="${ad.assetUrl}">
                    Your browser does not support the video tag.
                </video>
            `);
            videoElement.attr('data-video-uuid', ad.asset_uuid);
            videoElement.attr('data-video-name', ad.adname);

            if (
                labOptions.instance.get('viewModeScaleToFill') &&
                Signage.platform.type === Platform.BRIGHTSIGN
            ) {
                videoElement.attr('viewmode', 'scale-to-fill');
            }

            ad.videoElement = videoElement[0];
            ad.jqWrapperElement?.html(ad.videoElement);
        }

        ad.jqWrapperElement?.attr({
            'data-uuid': ad.asset_uuid,
            name: ad.adname,
        });
    }

    /**
     * Render ads to DOM
     */
    renderAds(): void {
        this.#adList.forEach((ad) => {
            ad.playcount = 0;
            ad.filetype = this.getAdFileType(ad);
            ad.assetUrl = this.getAssetUrl(ad);

            // find or create bundle
            let bundle = this.#bundles.find(function (bundle) {
                return bundle.id === ad.adbundleid;
            });

            if (typeof bundle === 'undefined') {
                //bundle has yet to be created, create it
                bundle = new AdBundle(ad.adbundleid);
                this.#bundles.push(bundle);
            }

            // an ad can only be put in a bundle once
            const matchingAdFoundInBundle = bundle.ads.find(function (adInBundle) {
                // Is adid already on the DOM?
                return ad.adid === adInBundle.adid;
            });

            // Add to the DOM for first time
            if (typeof matchingAdFoundInBundle === 'undefined') {
                ad.domRendered = true;
                bundle.ads.push(ad);

                ad.jqWrapperElement = $(`
                    <div
                        id='ad_${ad.adid}'
                        class='adcopy'
                        style='width:100%;height:100%;left:-9999999px;position:absolute;'
                    >
                    </div>
                `);

                $(this.wrapperElement).append(ad.jqWrapperElement);

                this.renderAd(ad);
            } else {
                if (!matchingAdFoundInBundle.domRendered) {
                    matchingAdFoundInBundle.domRendered = true;

                    matchingAdFoundInBundle.jqWrapperElement = $(`
                        <div
                            id='ad_${ad.adid}'
                            class='adcopy'
                            style='width:100%;height:100%;left:-9999999px;position:absolute;'
                        >
                        </div>
                    `);

                    $(this.wrapperElement).append(matchingAdFoundInBundle.jqWrapperElement);

                    this.renderAd(matchingAdFoundInBundle);
                }

                // update existing Ad(s)
                // Has the asset changed?
                if (ad.asset_uuid !== matchingAdFoundInBundle.asset_uuid) {
                    this.renderAd(ad);
                    matchingAdFoundInBundle.asset_uuid = ad.asset_uuid;
                }
            }
        });
    }

    /**
     * Remove ads from DOM that should not be there any more
     */
    pruneAds(): void {
        const allAdIds = this.#adList.map((ad) => ad.adid);

        this.#bundles.forEach(function (bundle) {
            const adsToDelete = bundle.ads.filter(function (ad) {
                return !allAdIds.includes(ad.adid);
            });

            adsToDelete.forEach(function (adToDelete) {
                adToDelete.jqWrapperElement?.remove();
            });

            bundle.removeAds(adsToDelete.map((ad) => ad.adid));
        });

        // keep only bundles having more than 0 ads
        this.#bundles = this.#bundles.filter(function (bundle) {
            return bundle.ads.length > 0;
        });
    }

    /**
     * determines slots to send to timeline
     *
     * NOTE: Duration is set through the ad widget configuration
     */
    querySlots(_numSlots: number, maxDuration: number): AdWidgetTimelineSlot[] {
        const slots: AdWidgetTimelineSlot[] = [];

        if (!this.#schedulerActionRanOnce) {
            // allow the schedule to run at least once even if the widget is paused
            // so that the widget will be able to at least know if it has content to
            // start displaying or not
            this.schedulerAction();
        }

        this.hasContentToPlay = true;

        if (this.#bundles.length === 0) {
            this.hasContentToPlay = false;
            return slots; //defaulting to [] as error
        }

        this.#bundles.forEach((bundle) => {
            if (bundle.ads.length > 0) {
                //get first saturation to get bundle saturation
                for (let i = 0; i < bundle.ads[0].saturation && maxDuration > 0; ++i) {
                    const next = bundle.next();

                    if (next.duration < maxDuration) {
                        //if we still haven't exceeded max duration...
                        next.playcount++;
                        //...increase playcount and add slot...
                        const slot = this.generateSlot(next.duration, undefined, { ad: next });
                        slots.push(slot);
                        //...and finally remove time from max duration
                        maxDuration -= next.duration;
                    } else {
                        break;
                    }
                }
            }
        });
        return slots;
    }

    /**
     * gets all available content to send to the timeline
     */
    querySlotsLinear(timeBased: boolean): AdWidgetTimelineSlot[] {
        const slots = [];

        if (!this.#schedulerActionRanOnce) {
            // allow the schedule to run at least once even if the widget is paused
            // so that the widget will be able to at least know if it has content to
            // start displaying or not
            this.schedulerAction();
        }

        this.hasContentToPlay = true;

        if (this.#bundles.length === 0) {
            this.hasContentToPlay = false;
            return []; //defaulting to [] as error
        }

        let usedTime = 0;

        if (timeBased) {
            this.#lastBundleIndex =
                this.#bundles.length > 0 && this.#lastBundleIndex < this.#bundles.length
                    ? this.#lastBundleIndex
                    : 0;

            if (this.#bundles.length > 0) {
                do {
                    const next = this.#bundles[this.#lastBundleIndex].next();

                    if (null == next) {
                        break;
                    }

                    //...increase playcount and add slot...
                    next.playcount++;

                    const slot = this.generateSlot(next.duration, undefined, { ad: next });
                    slots.push(slot);

                    this.#lastBundleIndex =
                        this.#lastBundleIndex === this.#bundles.length
                            ? 0
                            : this.#lastBundleIndex + 1;

                    usedTime += next.duration;
                } while (usedTime < this.minimumLoopTime);
            }
        } else {
            //get all available ads
            this.#bundles.forEach((bundle) => {
                if (bundle.ads.length > 0) {
                    for (let i = 0; i < bundle.ads.length; ++i) {
                        const next = bundle.next();

                        //...increase playcount and add slot...
                        next.playcount++;

                        const slot = this.generateSlot(next.duration, undefined, { ad: next });
                        slots.push(slot);
                    }
                }
            });
        }

        return slots;
    }

    /**
     * Move currentSlot into view. Start playback. (triggered before fade in occurs)
     */
    async beforeFadeIn(currentSlot: AdWidgetTimelineSlot): Promise<void> {
        if (!currentSlot) {
            throw new Error('currentSlot must be defined');
        }
        this.#startPlay = moment();

        currentSlot.params.ad.jqWrapperElement?.css('left', '0px');

        if (currentSlot.params.ad.filetype === 'video') {
            // Set current src
            const currentSource = currentSlot.params.ad.jqWrapperElement?.find('source');
            let sourcePrefix = '';

            if (Signage.platform instanceof Platform.ReachPlayer) {
                const prefetchComplete = Signage.platform.prefetchVideo(
                    currentSlot.params.ad.asset_uuid
                );
                // Only set video source if prefetch is complete
                if (prefetchComplete) {
                    sourcePrefix = 'http://localhost:23897/video?id=';
                }
            } else if (Signage.platform instanceof Platform.BrightSignPlayer) {
                sourcePrefix = 'file:///';
            } else {
                sourcePrefix = ReachAPI.server + '/cdn/asset/';
            }

            if (sourcePrefix !== '') {
                currentSource?.attr('src', sourcePrefix + currentSlot.params.ad.asset_uuid);
            }

            try {
                if (currentSlot.params.ad.videoElement) {
                    currentSlot.params.ad.videoElement.currentTime = 0;
                    currentSlot.params.ad.videoElement.play();
                }
            } catch (error: unknown) {
                Log.Error('Failed to play Ad.', null, __fileline, error);

                // try to re-create the video element (something about the element
                // is messed up)
                this.renderAd(currentSlot.params.ad);
            }
        }

        this.#adNowPlaying = true;
        this.#currentAd = currentSlot.params.ad;
    }

    /**
     * Log proof of play (triggered before fade out occurs)
     */
    async beforeFadeOut(currentSlot: AdWidgetTimelineSlot): Promise<void> {
        const ms = moment().diff(this.#startPlay);
        const duration = moment.duration(ms);

        Log.NamedEvent(NamedEventCode.SIGNAGE_PLAY_EVENT, 'ad played', 'ad.js', {
            Type: 'ad',
            Id: currentSlot.params.ad.adid,
            Name: currentSlot.params.ad.adname,
            WidgetId: this.id,
            StartTimeUtc: this.#startPlay?.unix() ?? 0,
            Duration: `${Math.floor(duration.asHours())}${moment.utc(ms).format(':mm:ss')}`,
        });

        this.#startPlay = null;
    }

    /**
     * Abort loop if an ad was changed (triggered once fade in of widget is
     * complete)
     */
    async afterFadeIn(): Promise<FadeInCommand> {
        this.#adDataChanged = false;
        return {
            abortLoop: this.#adDataChanged,
        };
    }

    /**
     * Move currentSlot out of view. Stop playback. Reset to start. (triggered once
     * fade out of widget is complete)
     */
    async afterFadeOut(currentSlot: AdWidgetTimelineSlot): Promise<void> {
        currentSlot.params.ad.jqWrapperElement?.css('left', '-9999999px');

        if (currentSlot.params.ad.filetype === 'video') {
            try {
                if (currentSlot.params.ad.videoElement) {
                    currentSlot.params.ad.videoElement.currentTime = 0;
                    currentSlot.params.ad.videoElement.pause();
                }
            } catch (error: unknown) {
                Log.Error('Failed to play Ad.', null, __fileline, error);
            }
        }

        this.#adNowPlaying = false;
        this.#currentAd = null;
    }

    /**
     * @inheritdoc
     */
    async _doPause(): Promise<void> {
        if (this.#adNowPlaying) {
            if (this.#currentAd?.filetype === 'video') {
                this.#currentAd.videoElement?.pause();
            }
        }
    }

    /**
     * @inheritdoc
     */
    async play(currentSlot: AdWidgetTimelineSlot): Promise<void> {
        await super.play(currentSlot);

        if (this.#adList !== null) {
            this.renderAds();
            this.pruneAds();
        }

        if (this.#adNowPlaying) {
            this.#currentAd?.jqWrapperElement?.css('left', '0px');

            if (this.#currentAd?.filetype === 'video') {
                this.#currentAd.videoElement?.play();
            }
        }
    }

    /**
     * @inheritdoc
     */
    async stop(): Promise<void> {
        // Since pause expects a slot parameter, we need to create a dummy slot
        this.pause({
            widget: this,
            duration: 0,
            params: {},
            groupIdentifier: null,
        });
        this.tellAdsNoDOM();
        return super.stop();
    }

    /**
     * sets the domRendered property on all of the ads to false
     */
    tellAdsNoDOM(): void {
        this.#bundles.forEach((bundle) =>
            bundle.ads.forEach((ad) => {
                ad.domRendered = false;
            })
        );
    }

    /**
     * @inheritdoc
     */
    schedulerAction(): void {
        this.#schedulerActionRanOnce = true;

        const getAdBundlesJqPromise = ReachAPI.ads.getAdsForWidget(
            this.id,
            Math.round(Date.now() / 1000),
            glom.timezone
        );

        getAdBundlesJqPromise.fail(
            (...args: [JQuery.jqXHR, string, string, string | null, Event | null]) => {
                Log.Warning(
                    'Failed to get Ads list for the Ads Widget.',
                    null,
                    'AdWidget.ts', // **See note about hardcoded source
                    ReachAPI.createPromiseFailObject.apply(this, args)
                );
            }
        );

        getAdBundlesJqPromise.done(async (data: { ads: AdObject[] }) => {
            if (typeof data.ads !== 'undefined') {
                this.#adList = data.ads;

                // Only change things if the ad data changes
                if (this.#stringifiedAdList !== JSON.stringify(this.#adList)) {
                    // Abort the loop if ad data changes, but not for the first time
                    if (null !== this.#stringifiedAdList) {
                        this.#adDataChanged = true;
                    }

                    this.#stringifiedAdList = JSON.stringify(this.#adList);

                    await this.getAdAssets();

                    this.renderAds();
                    this.pruneAds();
                }
            }
        });
    }
}
